import { Box, Flex } from '@chakra-ui/layout';
import {
  Button,
  FormControl,
  FormErrorMessage,
  IconButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  NumberInput,
  NumberInputField,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import currency from '@common/utility/currency';
import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useSurveyBannerModel } from '../models/SurveyBannerModel';

type AnnualIncomeProps = {
  modalProps: UseDisclosureProps;
};

type AnnualIncomeForm = {
  annualIncome: number;
};

export const AnnualIncome: React.FC<AnnualIncomeProps> = ({ modalProps }) => {
  const { investorAccount, updateAnnualIncome } = useInvestorAccountModel();
  const model = useSurveyBannerModel();
  const focusRef = useRef<HTMLInputElement>(null);
  const form = useForm<AnnualIncomeForm>({
    defaultValues: {
      annualIncome: investorAccount?.non_accredited_survey_v2?.annual_income?.value ?? undefined,
    },
    mode: 'onChange',
  });

  const annualIncome = form.watch('annualIncome');

  const submitAnnualIncome = async (annualIncome: number) => {
    if (annualIncome === investorAccount?.non_accredited_survey_v2?.annual_income?.value) {
      model.nextStep();
    } else {
      model.setLoading(true);
      const status = await updateAnnualIncome(annualIncome);
      if (status.success) {
        model.nextStep();
        model.setLoading(false);
      }
    }
  };

  useEffect(() => {
    focusRef.current?.focus();
  }, []);

  return (
    <ModalContent>
      <ModalHeader
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        borderBottomWidth={'1px'}
        borderBottomColor={'gray.300'}>
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="chevron-left" />}
          onClick={() => model.prevStep()}
        />
        Annual Income
        <IconButton
          color="black"
          variant={'ghost'}
          aria-label="Back"
          icon={<Icon icon="times" />}
          onClick={modalProps.onClose}
        />
      </ModalHeader>
      <ModalBody>
        <Flex flex={1} gap="24px" p={'24px'} justifyContent={'space-between'} flexDirection={'column'} display={'flex'}>
          <Box>
            <TextBlock weight="bold" fontSize={'20px'} lineHeight={'20px'}>
              What's your current income this year?
            </TextBlock>
            <TextBlock fontSize={'14px'}>Please enter your annual salary or what you made this year. </TextBlock>
          </Box>
          <FormControl isDisabled={model.loading} isInvalid={!!form.formState.errors.annualIncome}>
            <Controller
              control={form.control}
              name="annualIncome"
              rules={{
                max: {
                  message: 'Annual income must be less than or equal to $2,147,483,647',
                  value: 2147483647,
                },
                min: 0,
              }}
              render={({ field }) => (
                <NumberInput
                  inputMode="numeric"
                  isRequired={true}
                  isValidCharacter={(character) => !isNaN(Number(character))}
                  onChange={field.onChange}
                  precision={0}
                  step={5000}
                  value={
                    field.value != null ? currency.format(field.value ?? 0, { maximumFractionDigits: 0 }) : undefined
                  }>
                  <NumberInputField
                    autoFocus
                    onKeyUp={(e) => {
                      if (e.key === 'Enter' && Boolean(annualIncome)) {
                        submitAnnualIncome(annualIncome);
                      }
                    }}
                    placeholder={'$0.00'}
                    maxLength={20}
                    ref={focusRef}
                  />
                </NumberInput>
              )}
            />
            {form.formState.errors.annualIncome && (
              <FormErrorMessage>{form.formState.errors.annualIncome.message}</FormErrorMessage>
            )}
          </FormControl>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          autoFocus={true}
          isLoading={model.loading}
          isDisabled={!annualIncome}
          w={'100%'}
          rightIcon={<Icon icon="chevron-right" />}
          mt={'10px'}
          onClick={() => submitAnnualIncome(annualIncome)}>
          Continue
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
